import {useRef, useState} from "react";
import styled from "styled-components";
import {Tooltip as ReactTooltip} from "react-tooltip";
import {tokens} from "ui/blocks/guestMessaging/GuestUI";
import {USDollar} from "utils";

// Discount policies come from the price calculation ("First class", "3 classes").
const discountLabel = (policy) => {
    if (!policy) return "Discount";
    if (/^first class$/i.test(policy)) return "First-class discount";
    return `${policy} discount`;
};

/**
 * ⓘ next to the total. Hover (mouse), click, tap or Enter shows how the
 * total is made (all values from the booking price calculation, unchanged).
 * Open state is ours: on touch screens a tap would otherwise open and close it.
 */
export const PriceBreakdown = ({cost, classes, students, id = "booking-price-breakdown"}) => {
    const {classCost = 0, discount = 0, tax = 0, totalCost = 0, discountPolicy = ""} = cost || {};
    const [open, setOpen] = useState(false);
    const pointer = useRef(""); // pointer type of the press that led to the click
    const hover = (value) => (e) => { if (e.pointerType === "mouse") setOpen(value); };
    return (
        <>
            <InfoButton
                type="button"
                data-tooltip-id={id}
                aria-label="Price breakdown"
                aria-expanded={open}
                onPointerEnter={hover(true)}
                onPointerLeave={hover(false)}
                onPointerDown={(e) => { pointer.current = e.pointerType; }}
                onBlur={() => setOpen(false)}
                onClick={(e) => {
                    e.preventDefault();
                    // Mouse: hover already opened it, a click keeps it open. Touch/keyboard: toggle.
                    const mouse = pointer.current === "mouse";
                    pointer.current = "";
                    setOpen((o) => (mouse ? true : !o));
                }}
            >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9.5"/>
                    <path d="M12 11v5.5"/>
                    <path d="M12 7.6v.1"/>
                </svg>
            </InfoButton>
            <StyledTooltip
                id={id}
                place="top-end"
                positionStrategy="fixed"
                isOpen={open}
                setIsOpen={setOpen}
                openEvents={{}}
                closeEvents={{}}
                globalCloseEvents={{escape: true, clickOutsideAnchor: true}}
                opacity={1}
            >
                <Rows>
                    <Row>
                        <span>
                            Class price
                            {(classes > 1 || students) ? <Detail>{[classes > 1 ? `${classes} classes` : null, students].filter(Boolean).join(" · ")}</Detail> : null}
                        </span>
                        <span>{USDollar.format(classCost)}</span>
                    </Row>
                    {discount > 0 && (
                        <Row $accent>
                            <span>{discountLabel(discountPolicy)}</span>
                            <span>−{USDollar.format(discount)}</span>
                        </Row>
                    )}
                    {tax > 0 && (
                        <Row>
                            <span>Tax</span>
                            <span>{USDollar.format(tax)}</span>
                        </Row>
                    )}
                    <Row $total>
                        <span>Total</span>
                        <span>{USDollar.format(totalCost)}</span>
                    </Row>
                </Rows>
            </StyledTooltip>
        </>
    );
};

const InfoButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin: 0 -4px 0 2px;
    padding: 0;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: ${tokens.muted};
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:hover, &:focus-visible {
        color: ${tokens.accent};
        background: ${tokens.accentSoft};
        outline: none;
    }
`;

const StyledTooltip = styled(ReactTooltip)`
    &&& {
        z-index: 2000;
        width: 240px;
        max-width: calc(100vw - 32px);
        box-sizing: border-box;
        padding: 12px 14px;
        border-radius: 12px;
        background: #fff;
        color: ${tokens.text};
        font-family: var(--font-primary);
        box-shadow: 0 8px 28px rgba(16, 16, 40, 0.16), 0 1px 3px rgba(16, 16, 40, 0.08);
    }
`;

const Rows = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 14px;
`;

const Row = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    color: ${({$accent}) => ($accent ? tokens.accent : tokens.text)};

    > span:last-child {
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
    }

    ${({$total}) => $total && `
        margin-top: 4px;
        padding-top: 8px;
        border-top: 1px solid ${tokens.border};
        font-weight: 600;
        color: ${tokens.ink};
    `}
`;

const Detail = styled.span`
    display: block;
    font-size: 12px;
    color: ${tokens.muted};
`;
